import type { IInvestmentProposalRepository } from "@/application/ports/repositories";
import type {
  InvestmentProposalCreateRequest,
  InvestmentProposalResponse,
  InvestmentProposalListResponse,
} from "@/domain/investmentProposal";
import { apiClient, ApiClientError } from "../client";
import { getLogger } from "@/infrastructure/di/services";

export class InvestmentProposalRepository implements IInvestmentProposalRepository {
  private basePath = "/investments/proposals";
  private validateResponse<T>(data: unknown, requiredKeys: string[]): T {
    if (!data || typeof data !== "object") {
      throw new ApiClientError({
        message: "Invalid response format",
        status: 500,
        code: "INVALID_RESPONSE",
        details: { received: data },
      });
    }
    const missingKeys = requiredKeys.filter((key) => !(key in (data as object)));
    if (missingKeys.length > 0) {
      throw new ApiClientError({
        message: "Response missing required fields",
        status: 500,
        code: "INCOMPLETE_RESPONSE",
        details: { missingKeys, received: data },
      });
    }
    return data as T;
  }

  private handleError(error: unknown, operation: string): never {
    getLogger().error(`Investment proposal ${operation} failed:`, error);
    if (error instanceof ApiClientError) throw error;
    if (error instanceof Error) {
      throw new ApiClientError({
        message: error.message || `Failed to ${operation}`,
        status: 0,
        code: "NETWORK_ERROR",
        details: { originalError: error.message },
      });
    }
    throw new ApiClientError({
      message: `An unexpected error occurred during ${operation}`,
      status: 0,
      code: "UNKNOWN_ERROR",
      details: { error: String(error) },
    });
  }

  async createProposal(data: InvestmentProposalCreateRequest): Promise<InvestmentProposalResponse> {
    try {
      const response = await apiClient.post<InvestmentProposalResponse>(this.basePath, data);
      return this.validateResponse<InvestmentProposalResponse>(response.data, [
        "uuid",
        "investor",
        "organization",
        "fiscal_year",
        "taxable_base",
        "tax_rate",
        "status",
        "expires_at",
        "created_at",
        "updated_at",
      ]);
    } catch (error) {
      throw this.handleError(error, "create investment proposal");
    }
  }

  async getProposals(params?: {
    limit?: number;
    offset?: number;
  }): Promise<InvestmentProposalListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
      if (params?.offset !== undefined) queryParams.append("offset", params.offset.toString());

      const url = queryParams.toString()
        ? `${this.basePath}?${queryParams.toString()}`
        : this.basePath;

      const response = await apiClient.get<InvestmentProposalListResponse>(url);
      return this.validateResponse<InvestmentProposalListResponse>(response.data, [
        "proposals",
        "total",
        "limit",
        "offset",
      ]);
    } catch (error) {
      throw this.handleError(error, "fetch investment proposals");
    }
  }

  async getProposal(uuid: string, token: string): Promise<InvestmentProposalResponse> {
    try {
      const response = await apiClient.get<InvestmentProposalResponse>(
        `${this.basePath}/${uuid}?token=${encodeURIComponent(token)}`,
      );
      return this.validateResponse<InvestmentProposalResponse>(response.data, [
        "uuid",
        "investor",
        "organization",
        "fiscal_year",
        "taxable_base",
        "tax_rate",
        "status",
        "expires_at",
        "created_at",
        "updated_at",
      ]);
    } catch (error) {
      throw this.handleError(error, `fetch investment proposal ${uuid}`);
    }
  }

  async acceptProposal(uuid: string, token: string): Promise<void> {
    try {
      await apiClient.post(`${this.basePath}/${uuid}/accept?token=${encodeURIComponent(token)}`);
    } catch (error) {
      throw this.handleError(error, `accept investment proposal ${uuid}`);
    }
  }

  async declineProposal(uuid: string, token: string): Promise<void> {
    try {
      await apiClient.post(`${this.basePath}/${uuid}/decline?token=${encodeURIComponent(token)}`);
    } catch (error) {
      throw this.handleError(error, `decline investment proposal ${uuid}`);
    }
  }
}
